import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { Building2, Loader2 } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type { PublicApplyMeResponse } from "@eduviaos/shared/types";
import { fetchApplyMe } from "@/lib/api";
import { parsePublicError } from "@/lib/error-utils";
import { PageCard } from "@/components/AppFrame";
import { ErrorState } from "@/components/ErrorState";
import { ApplyFormSection } from "@/components/apply/ApplyFormSection";
import { ApplyAttemptSection } from "@/components/apply/ApplyAttemptSection";
import { ApplyStatusSection } from "@/components/apply/ApplyStatusSection";

/**
 * Phase F4 FE — single entry point for the parent apply portal.
 *
 * Tokens are minted by IA staff via the `POST /admissions/enquiries/{id}/
 * apply-tokens` endpoint and emailed to the parent. The token type
 * (`apply_form`, `attempt_assessment`, `view_status`) is opaque to the
 * caller but the BE returns it via `GET /apply/{token}/me`.
 *
 * Routing strategy (D2d compliant):
 *   - `/apply/:token` is a single route — one URL = one capability.
 *   - We dispatch INSIDE the page based on `purpose` rather than
 *     burning three different URL paths because the token itself
 *     carries the capability and we don't want parents copy-pasting
 *     between different surfaces.
 *   - Tenant slug comes from the host name (`<slug>--apply.eduviaos.com`),
 *     never the URL path. The BE rejects cross-tenant tokens at the
 *     edge (the Worker overwrites the X-Tenant-Slug header before
 *     forwarding to the BE), so the FE just trusts whatever the
 *     handshake returned.
 */
export function ApplyPage() {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";
  const { token = "" } = useParams<{ token: string }>();

  const meQuery = useQuery({
    queryKey: ["public-apply-me", tenantSlug, token],
    queryFn: () => fetchApplyMe(token),
    enabled: !!tenantSlug && !!token,
    // `me` is allowed to be a touch stale — it only changes when the
    // token is revoked or consumed, both of which downstream sub-pages
    // re-fetch on. 30s is a sweet spot between freshness and avoiding
    // flicker when a parent navigates back to the entry route.
    staleTime: 30_000,
  });

  if (!tenantSlug) {
    return (
      <ErrorState
        title="Open your institution's apply link"
        body={
          <>
            Apply links live on each institution's surface (e.g.{" "}
            <code className="bg-muted/40 rounded px-1 py-0.5 font-mono text-xs">
              acme-school--apply.eduviaos.com
            </code>
            ). Open the link the institution sent you to continue.
          </>
        }
        cta={
          <Button asChild variant="outline">
            <Link to="/welcome">About EduViaOS</Link>
          </Button>
        }
      />
    );
  }

  if (!token) {
    return (
      <ErrorState
        title="Missing apply token"
        body="The apply URL needs to include a token. Open the link the institution emailed you."
      />
    );
  }

  if (meQuery.isLoading) {
    return (
      <PageCard>
        <div
          role="status"
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
        >
          <Loader2 className="text-primary h-4 w-4 animate-spin" />
          <span>Loading your apply page…</span>
        </div>
      </PageCard>
    );
  }

  if (meQuery.error) {
    return <ApplyTokenError err={meQuery.error} onRetry={() => meQuery.refetch()} />;
  }

  const me = meQuery.data;
  if (!me) {
    return (
      <ErrorState
        title="Apply page unavailable"
        body="We couldn't load this apply page. Please refresh, or try again in a few minutes."
        cta={<Button onClick={() => meQuery.refetch()}>Retry</Button>}
      />
    );
  }

  return <ApplyDispatch me={me} token={token} />;
}

// ── Token-level error rendering ────────────────────────────────────────

function ApplyTokenError({ err, onRetry }: { err: unknown; onRetry: () => void }) {
  const parsed = parsePublicError(err);
  if (parsed.kind === "tenant_or_form_not_found") {
    return (
      <ErrorState
        title="Link not recognised"
        body={
          <>
            We couldn't find an active apply session for this link. Either it has been revoked,
            already used, or belongs to a different institution. Please contact the institution if
            you believe this is a mistake.
          </>
        }
      />
    );
  }
  if (parsed.code === "APPLY_TOKEN_EXPIRED") {
    return (
      <ErrorState
        title="Link expired"
        body="This apply link has expired. Please contact the institution to send a new one."
      />
    );
  }
  if (parsed.code === "APPLY_TOKEN_CONSUMED") {
    return (
      <ErrorState
        title="Link already used"
        body={
          <>
            Each apply link can only be used once. If you submitted the form, you should have
            received a tracker link. To check status, ask the institution to issue a status link.
          </>
        }
      />
    );
  }
  if (parsed.kind === "network") {
    return (
      <ErrorState
        title="Can't reach EduViaOS"
        body={parsed.message}
        cta={<Button onClick={onRetry}>Try again</Button>}
      />
    );
  }
  return (
    <ErrorState
      title="Couldn't load apply page"
      body={parsed.message}
      cta={<Button onClick={onRetry}>Reload</Button>}
    />
  );
}

// ── Per-purpose dispatch ──────────────────────────────────────────────

function ApplyDispatch({ me, token }: { me: PublicApplyMeResponse; token: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0.32, 1] }}
      className="grid gap-4"
    >
      <InstitutionHeader name={me.tenant_name} stage={me.stage.label} />

      {me.purpose === "apply_form" && <ApplyFormSection token={token} me={me} />}
      {me.purpose === "attempt_assessment" && <ApplyAttemptSection token={token} me={me} />}
      {me.purpose === "view_status" && <ApplyStatusSection token={token} me={me} />}
    </motion.div>
  );
}

function InstitutionHeader({ name, stage }: { name: string; stage: string }) {
  return (
    <header className="border-border/60 bg-card/60 flex items-center gap-3 rounded-xl border p-4 shadow-sm">
      <span
        aria-hidden
        className="bg-primary/10 text-primary flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
      >
        <Building2 className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-foreground truncate text-base font-semibold leading-tight">{name}</div>
        <div className="text-muted-foreground truncate text-xs">Current stage: {stage}</div>
      </div>
    </header>
  );
}
